import { Button } from "@shared-ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@shared-ui/card";
import { Label } from "@shared-ui/label";
import { Skeleton } from "@shared-ui/skeleton";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { CategoryPicker } from "@/features/catalog/components/category-picker";
import {
	assignBrandCategory,
	type BrandCategoryCoverage,
	getBrandCategoryCoverage,
	removeBrandCategory,
} from "@/features/catalog/services/catalog-api.service";
import { apiErrorMessage } from "@/lib/api-error";

const SOURCE_LABEL: Record<BrandCategoryCoverage["source"], string> = {
	"brand-master": "Brand master",
	manual: "Added here",
};

/**
 * Where a brand sells, and what we hold there (issue #255).
 *
 * The row that matters is the one at zero: an assigned category with nothing
 * loaded is a job, and before `catalog_brand_categories` it could not be told
 * apart from a category the brand does not sell in at all.
 *
 * Shown to anyone who can open the editor, because coverage is a read and the
 * person loading the catalogue is the one who needs it. Adding and removing
 * cost `admin:catalog:brands`, like the server's routes.
 */
export function BrandCategoryPanel({
	brandId,
	canEdit,
}: {
	brandId: string;
	canEdit: boolean;
}) {
	const queryClient = useQueryClient();
	const queryKey = ["admin-brand-coverage", brandId];
	const [error, setError] = useState<string | null>(null);

	const coverage = useQuery({
		queryKey,
		queryFn: () => getBrandCategoryCoverage(brandId),
	});

	const assign = useMutation({
		mutationFn: (categoryId: string) =>
			assignBrandCategory(brandId, categoryId),
		// A coverage read still in flight was asked before this change, and
		// would land on top of the PUT's answer below, taking the new row away.
		onMutate: () => queryClient.cancelQueries({ queryKey }),
		// The PUT answers with the whole coverage, so the panel redraws from it
		// rather than paying a second read.
		onSuccess: (next: BrandCategoryCoverage[]) => {
			queryClient.setQueryData(queryKey, next);
		},
		onError: (err: unknown) => {
			setError(apiErrorMessage(err, "Could not add that category."));
			// The cancel above reverts a first load that had not landed back to
			// "pending", and a refused PUT writes nothing in its place -- so ask
			// again, or the panel waits on a read nobody is making.
			queryClient.invalidateQueries({ queryKey });
		},
	});

	const remove = useMutation({
		mutationFn: (categoryId: string) =>
			removeBrandCategory(brandId, categoryId),
		// No cancel needed here, unlike `assign`: a Remove button exists only
		// once coverage has data, and with data this invalidation cancels an
		// in-flight read itself before reading afresh.
		onSuccess: () => queryClient.invalidateQueries({ queryKey }),
		onError: (err: unknown) => {
			setError(apiErrorMessage(err, "Could not remove that category."));
			// A refusal usually means the row changed under the editor -- a seed
			// run removed it, say -- so show what is there now rather than a row
			// every further click will be refused on.
			queryClient.invalidateQueries({ queryKey });
		},
	});

	const rows = coverage.data ?? [];
	const busy = assign.isPending || remove.isPending;

	return (
		<Card>
			<CardHeader>
				<CardTitle>Categories</CardTitle>
				<CardDescription>
					Where this brand sells, and what is loaded for it. A category with
					nothing loaded is a gap to fill.
				</CardDescription>
			</CardHeader>
			<CardContent className="grid gap-4">
				{error ? (
					<div
						role="alert"
						className="rounded-2xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
					>
						{error}
					</div>
				) : null}

				{coverage.isPending ? (
					<Skeleton className="h-24 w-full" />
				) : coverage.isError ? (
					<div className="text-sm text-red-700">
						Could not load this brand's categories.{" "}
						<button
							type="button"
							className="underline"
							onClick={() => coverage.refetch()}
						>
							Try again
						</button>
					</div>
				) : rows.length === 0 ? (
					<p className="text-sm text-muted-foreground">
						No categories assigned yet.
					</p>
				) : (
					<table className="w-full text-sm">
						<thead className="text-left text-xs text-muted-foreground">
							<tr>
								<th className="py-1 font-normal">Category</th>
								<th className="py-1 text-right font-normal">Products</th>
								<th className="py-1 text-right font-normal">Live</th>
								<th className="py-1 pl-4 font-normal">From</th>
								{canEdit ? (
									<th className="py-1">
										<span className="sr-only">Actions</span>
									</th>
								) : null}
							</tr>
						</thead>
						<tbody>
							{rows.map((row) => (
								<tr key={row.categoryId} className="border-t">
									<td className="py-2">
										<span className="flex flex-wrap items-center gap-2">
											{row.name}
											{row.productCount === 0 ? (
												<Badge variant="warning">Nothing loaded</Badge>
											) : row.activeCount === 0 ? (
												<Badge variant="warning">None live</Badge>
											) : null}
										</span>
									</td>
									<td className="py-2 text-right tabular-nums">
										{row.productCount}
									</td>
									<td className="py-2 text-right tabular-nums">
										{row.activeCount}
									</td>
									<td className="py-2 pl-4 text-muted-foreground">
										{SOURCE_LABEL[row.source]}
									</td>
									{canEdit ? (
										<td className="py-2 text-right">
											<Button
												variant="outline"
												size="sm"
												aria-label={`Remove ${row.name}`}
												disabled={busy}
												onClick={() => {
													setError(null);
													remove.mutate(row.categoryId);
												}}
											>
												Remove
											</Button>
										</td>
									) : null}
								</tr>
							))}
						</tbody>
					</table>
				)}

				{/*
				 * Whenever removal is offered, not only beside "Brand master" rows:
				 * an operator's own row for a pair the brand master also names stays
				 * "Added here" through a seed run, and comes back after removal too.
				 */}
				{canEdit && rows.length > 0 ? (
					<p className="text-xs text-muted-foreground">
						A category the brand master names for this brand comes back the next
						time the brand master is seeded, even after you remove it.
					</p>
				) : null}

				{canEdit ? (
					<div className="grid gap-1.5 md:max-w-sm">
						<Label htmlFor="add-brand-category">Add a category</Label>
						<CategoryPicker
							id="add-brand-category"
							value={null}
							valueLabel={null}
							clearable={false}
							disabled={busy}
							onChange={(category) => {
								if (!category) return;
								setError(null);
								assign.mutate(category.id);
							}}
						/>
					</div>
				) : null}
			</CardContent>
		</Card>
	);
}
